import { useCallback, useState, type ReactNode, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { CurrencyUtils } from '@shared/utils';
import type { RecipientResponse } from '@shared/api/rest/recipients';
import { ColorsEnum, Icon, NoData, SearchInput, Text } from '..';
import { Modal } from '../modal';
import SwapIcon from '@assets/icons/swap.svg';

interface ISelectRecipientProps {
  loading?: boolean;
  label: ReactNode;
  items: RecipientResponse[];
  value: RecipientResponse | null;
  onChange: (value: RecipientResponse | null) => void;
  searchValue: string;
  onSearch: (value: string) => void;
}

const defaultTitle = 'Select recipient';

export const SelectRecipient = (props: ISelectRecipientProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [itemsValues, setItemsValues] = useState(props.items);
  const title = useMemo(() => props.label ?? defaultTitle, [props.label]);

  useEffect(() => {
    setItemsValues(props.items);
  }, [props.items]);

  const onSelect = useCallback(
    (value: RecipientResponse | null) => {
      props.onChange && props.onChange(value);
      setOpen(false);
    },
    [props.onChange],
  );

  return (
    <>
      <Text className='mb-3'>{title}</Text>
      <AccountListItem className='bg-transparent' value={props.value} isShowEndIcon onClick={() => setOpen(true)} />
      <Modal isOpen={open} title='Saved Recipients' onClose={() => setOpen(false)} width='450px' position='top'>
        <SearchInput value={props.searchValue} onChange={props.onSearch} />
        <div className='flex-1 no-scrollbar overflow-y-auto'>
          {props.value && <AccountListItem className='bg-white' isShowEndIcon={false} onClick={() => onSelect(null)} />}
          {itemsValues.map((x, key) => (
            <AccountListItem className='bg-white' key={key} value={x} isShowEndIcon={false} onClick={() => onSelect(x)} />
          ))}
          {itemsValues.length === 0 && <NoData />}
        </div>
      </Modal>
    </>
  );
};

interface IAccountListItemProps {
  className?: string;
  value?: RecipientResponse | null;
  isShowEndIcon?: boolean;
  onClick: () => void;
}

const AccountListItem = (props: IAccountListItemProps) => {
  const isHasIndividual = props.value && props.value.firstName?.length > 0 && props.value.lastName?.length > 0;
  return (
    <div
      className={clsx('flex flex-row items-center my-2 dark:bg-soft-black bg-white cursor-pointer w-full px-3 rounded-[2px]', props.className)}
      onClick={props.onClick}
    >
      <div className='flex bg-transparent mr-2 cursor-pointer w-full py-3 pr-3'>
        {props.value ? (
          <div className='flex item-center gap-2'>
            <div className=' min-w-11 min-h-11 w-11 h-11 rounded-full bg-primary flex'>
              <Text className='m-auto' weight='semibold' color={ColorsEnum.White}>
                {isHasIndividual
                  ? `${props.value.firstName[0].toUpperCase()}${props.value.lastName[0].toUpperCase()}`
                  : props.value.companyName[0].toUpperCase()}
              </Text>
            </div>
            <div className='h-full w-full flex flex-col justify-center'>
              <Text weight='semibold'>{isHasIndividual ? `${props.value.firstName} ${props.value.lastName}` : props.value.companyName}</Text>
              <Text weight='semibold' size='xs' color={ColorsEnum.Gray}>
                {props.value.accountNumber || props.value.iban || '-'}
              </Text>
            </div>
          </div>
        ) : (
          <div className='flex items-center gap-2'>
            <div className='w-11 h-11 rounded-full border-2 border-dashed border-primary flex items-center justify-center'>
              <Text weight='semibold' className='m-auto' size='3xl' color={ColorsEnum.Primary}>
                +
              </Text>
            </div>
            <Text weight='semibold'>New recipient</Text>
          </div>
        )}
      </div>
      {props.value?.currency && (
        <div className='min-w-16 w-16 max-w-16 h-7 p-2 rounded-[5px] border border-primary flex items-center justify-center'>
          <Text className='mr-2' size='xs' weight='semibold' color={ColorsEnum.Primary}>
            {CurrencyUtils.getSymbolByCode(props.value?.currency)}
          </Text>
          <Text size='xs' weight='semibold' isUppercase color={ColorsEnum.Primary}>
            {props.value?.currency}
          </Text>
        </div>
      )}
      {props.isShowEndIcon && (
        <div className='pl-4 my-auto'>
          <Icon icon={<SwapIcon width='24' height='24' />} className='rotate-90' />
        </div>
      )}
    </div>
  );
};
